import React from "react";
import {
    Box,
    Heading,
    Text,
    Stack,
    List,
    chakra,
    VStack,
    AspectRatio,
} from "@chakra-ui/react";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";
import CTA from "../components/CTA";

const ComputerVisionPage = () => {
    return (
        <Box>
            <SectionCard flexDirection="row" delay={0}>
                <Box flex="2" textAlign={{base: "center", md: "left"}}>
                    <chakra.img
                        src="./CAAT_CV_Logo.png"
                        alt="Why Computer Vision?"
                        maxW="400px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        Why Computer Vision?
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Professional handlers already work visually. They read posture, movement,
                            orientation, speed, hesitation, focus, and interaction with the environment —
                            often without thinking about it.
                        </Text>

                        <Text fontSize="md">
                            Computer vision starts from the same source of information: the visible
                            behavior of the dog. The difference is not that the computer replaces the
                            handler’s judgement. The difference is that selected observations can be{" "}
                            <Highlight>captured, measured, and reused</Highlight>.
                        </Text>

                        <Text fontSize="md">
                            That makes video useful beyond the moment. It can support{" "}
                            <Highlight>automatic documentation</Highlight>, structured review, comparison
                            across sessions, and analysis that connects training events to progress over
                            time.
                        </Text>

                        <Text fontSize="md">
                            The goal is simple: keep the handler focused on the dog and the training
                            process, while the system helps turn visual information into{" "}
                            <Highlight>training data</Highlight>.
                        </Text>
                    </Stack>
                </Box>
            </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.2}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./IronDog_Demo.png"
                        alt="Seeing Risk Before the Miss"
                        maxW="450px"
                        mx="auto"
                    />
                </Box>

                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="xl" mb={4}>
                        Seeing Risk Before the Miss
                    </Heading>

                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Detection performance can decline while the dog is working. Sometimes the
                            first clear sign is a missed indication. But movement may already begin to
                            change before that moment.
                        </Text>

                        <Text fontSize="md">
                            We used <Highlight>computer vision</Highlight> to test this. Video from
                            detection dogs on an olfacto-treadmill was converted into body landmarks and{" "}
                            <Highlight>movement trajectories</Highlight>, then analyzed before each odor
                            presentation.
                        </Text>

                        <Text fontSize="md">
                            Recent movement patterns contained information about whether a missed
                            indication was more likely. The system does not predict a single mistake with
                            certainty; it estimates whether <Highlight>risk is beginning to build</Highlight>.
                        </Text>

                        <Text fontSize="md">
                            The current prototype <Highlight>runs in real time</Highlight>. While the dog
                            is running, video is processed live, movement information is updated
                            continuously, and the readiness estimate changes as new behavior comes in.
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            Research background:{" "}
                            <chakra.a
                                href="https://www.biorxiv.org/content/10.1101/2025.10.31.685787v2"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="brand.green.600"
                                fontWeight="medium"
                                _hover={{textDecoration: "underline"}}
                            >
                                preprint on detection dog readiness estimation
                            </chakra.a>
                        </Text>
                    </Stack>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row" delay={0.4}>
                <Box flex="2" textAlign="center">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Computer vision demo"
                            src="https://www.youtube.com/embed/gxt37P68jkY"
                            allowFullScreen
                        />
                    </AspectRatio>
                </Box>

                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="xl" mb={4}>
                        From Training Video to Training Data
                    </Heading>

                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Dog training is not only about the dog. A response depends on the cue, the
                            trainer’s timing, the dog’s attention, and what happens immediately before
                            and after the behavior.
                        </Text>

                        <Text fontSize="md">
                            This prototype explores whether ordinary training video can be turned into{" "}
                            <Highlight>structured training data</Highlight>. It analyzes selected parts
                            of the trainer–dog interaction: which cue was given, how the dog responded,
                            how long the response lasted, and whether the dog was focused at the moment
                            of the cue.
                        </Text>

                        <Text fontSize="md">
                            The result is not just a video clip, but a training record:{" "}
                            <Highlight>cue, latency, duration, and focus</Highlight> — data that can be
                            stored, reviewed, and compared across repetitions.
                        </Text>

                        <Text fontSize="md">
                            This is a feasibility study, not a finished product. But it shows the
                            direction: documentation can become less manual, and future training
                            adjustments can be based on <Highlight>recorded data</Highlight> rather than
                            memory alone.
                        </Text>
                    </Stack>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.5}>
                <Box flex="2" textAlign={{base: "center", md: "left"}}>
                    <chakra.img
                        src="./cv_distance.png"
                        alt="Measuring distances with computer vision"
                        maxW="400px"
                        mx="auto"
                    />
                </Box>

                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="xl" mb={4}>
                        Connecting Observation to Adaptation
                    </Heading>

                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Computer vision can capture much of what happens on the outside: posture,
                            movement, cue timing, response behavior, and the interaction between dog and
                            trainer. But not every relevant signal is visible.
                        </Text>

                        <Text fontSize="md">
                            Video can be combined with additional information such as{" "}
                            <Highlight>heart rate</Highlight>, <Highlight>body temperature</Highlight>,
                            or wearable movement data. These signals can add context when the dog’s
                            working state changes over time.
                        </Text>

                        <Text fontSize="md">
                            The real value emerges when these observations become part of the training
                            record. They can be <Highlight>stored, reviewed, and compared</Highlight>{" "}
                            across repetitions, dogs, and training conditions.
                        </Text>

                        <Text fontSize="md">
                            The long-term goal is not automated training. It is adaptive support: a
                            system that helps handlers connect what happened during training with what
                            should happen next — while the handler remains the decision maker.
                        </Text>
                    </Stack>
                </VStack>
            </SectionCard>
        </Box>
    );
};

export default ComputerVisionPage;
